/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GenAILiveClient } from "../lib/ai/genai-live-client";
import { LiveClientOptions } from "../types";
import { AudioStreamer } from "../lib/audio-streamer";
import { audioContext } from "../lib/utils";
import VolMeterWorket from "../lib/worklets/vol-meter";
import { LiveConnectConfig } from "@google/genai";
import { useAPIClient } from "../lib/api-client";
import { useAIUsage } from "../contexts/AIUsageContext";

export type UseLiveAPIResults = {
  client: GenAILiveClient;
  setConfig: (config: LiveConnectConfig) => void;
  config: LiveConnectConfig;
  model: string;
  setModel: (model: string) => void;
  connected: boolean;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  volume: number;
};

export function useLiveAPI(options: LiveClientOptions): UseLiveAPIResults {
  const { tracker } = useAIUsage();
  const client = useMemo(() => {
    // Use the enhanced client with usage tracking
    return new GenAILiveClient(options, tracker);
  }, [options, tracker]);
  const apiClient = useAPIClient();
  const audioStreamerRef = useRef<AudioStreamer | null>(null);

  const [model, setModel] = useState<string>("models/gemini-2.0-flash-exp");
  const [config, setConfig] = useState<LiveConnectConfig>({});
  const [connected, setConnected] = useState(false);
  const [volume, setVolume] = useState(0);

  // register audio for streaming server -> speakers
  useEffect(() => {
    if (!audioStreamerRef.current) {
      audioContext({ id: "audio-out" }).then((audioCtx: AudioContext) => {
        audioStreamerRef.current = new AudioStreamer(audioCtx);
        audioStreamerRef.current
          .addWorklet<any>("vumeter-out", VolMeterWorket, (ev: any) => {
            setVolume(ev.data.volume);
          })
          .then(() => {
            // Successfully added worklet
          });
      });
    }
  }, [audioStreamerRef]);

  useEffect(() => {
    const onOpen = () => {
      setConnected(true);
    };

    const onClose = () => {
      setConnected(false);
    };

    const onError = (error: ErrorEvent) => {
      console.error("error", error);
    };

    const stopAudioStreamer = () => audioStreamerRef.current?.stop();

    const onAudio = (data: ArrayBuffer) => audioStreamerRef.current?.addPCM16(new Uint8Array(data));

    client
      .on("error", onError)
      .on("open", onOpen)
      .on("close", onClose)
      .on("interrupted", stopAudioStreamer)
      .on("audio", onAudio);

    return () => {
      client
        .off("error", onError)
        .off("open", onOpen)
        .off("close", onClose)
        .off("interrupted", stopAudioStreamer)
        .off("audio", onAudio)
        .disconnect();
    };
  }, [client]);

  const connect = useCallback(async () => {
    if (!config) {
      throw new Error("config has not been set");
    }

    try {
      // 1. Fetch the ephemeral token from our backend
      const tokenResult = await apiClient.getLiveAPIToken();
      if (!tokenResult.success) {
        console.error("Failed to get ephemeral token:", tokenResult.error);

        // Handle specific authentication errors
        if (
          tokenResult.error?.message?.includes("Authentication required") ||
          tokenResult.error?.message?.includes("authorization header")
        ) {
          console.warn("User is not authenticated. Please sign in to connect.");
          // You could dispatch an event here or set some state to show login UI
          return;
        }

        // Handle other errors
        console.error("API error:", tokenResult.error?.message);
        return;
      }

      const ephemeralToken = tokenResult.data.token;

      // 2. Use the ephemeral token to connect directly to Gemini
      await client.disconnect();
      // The GenAILiveClient needs to be configured to use this token.
      // This assumes the client's `connect` method or options can accept a token.
      // We may need to adjust the GenAILiveClient library or its options.
      const connectOptions = { ...config, token: ephemeralToken };

      // Determine feature based on config (voice, screen share, etc)
      let feature = "voice_chat";
      if (
        config.systemInstruction &&
        typeof config.systemInstruction === "object" &&
        "parts" in config.systemInstruction
      ) {
        if (config.systemInstruction.parts?.some((p: any) => p.text?.includes("screen"))) {
          feature = "screen_share";
        } else if (config.systemInstruction.parts?.some((p: any) => p.text?.includes("webcam"))) {
          feature = "webcam";
        }
      }

      await client.connect(model, connectOptions, feature);
    } catch (error) {
      console.error("Connection failed:", error);
      // Ensure we're in a disconnected state if connection fails
      setConnected(false);
    }
  }, [client, config, model, apiClient, setConnected]);

  const disconnect = useCallback(async () => {
    await client.disconnect();
    setConnected(false);
  }, [setConnected, client]);

  return {
    client,
    config,
    setConfig,
    model,
    setModel,
    connected,
    connect,
    disconnect,
    volume,
  };
}
